import { getMfaTranslations } from './translations';
import {
  addedSubTitleForSMSAuthScreen,
  changeMFAButtonsText,
} from '../../login/helpers';
import { WIDGET_STATES } from '../config/widgetStates';

// **See: docs/okta-afterRender-controller-bug.md for full explanation**
export const resolveControllerName = (ctx, container) => {
  const raw = ctx?.controller;

  if (typeof raw === 'string') return raw;

  if (typeof raw === 'function' && container) {
    const controllerEl = container.querySelector('.auth-content-inner > [class]');
    const resolved = controllerEl?.className?.trim().split(/\s+/)[0];
    if (resolved) return resolved;
  }

  if (typeof ctx?.formName === 'string') return ctx.formName;

  return '';
};

// DOM Selectors
const USERNAME_SELECTORS = [
  'input[name="identifier"]',
  'input[name="username"]',
  'input[type="text"]',
];

const PASSWORD_SELECTORS = [
  'input[name="credentials.passcode"]',
  'input[name="password"]',
  'input[type="password"]',
];

const SUBMIT_SELECTORS = [
  'input[type="submit"]',
  'button[type="submit"]',
  'a[data-type="save"]',
];

const REMEMBER_ME_SELECTORS = ['input[name="remember"]'];

// DOM Query Helpers
export const findElementBySelectors = (container, selectors) => {
  for (const selector of selectors) {
    const element = container.querySelector(selector);
    if (element) return element;
  }
  return null;
};

export const findUsernameInput = container =>
  findElementBySelectors(container, USERNAME_SELECTORS);

export const findPasswordInput = container =>
  findElementBySelectors(container, PASSWORD_SELECTORS);

export const findSubmitButton = container =>
  findElementBySelectors(container, SUBMIT_SELECTORS);

export const findRememberMeCheckbox = container =>
  findElementBySelectors(container, REMEMBER_ME_SELECTORS);

export const findBackLink = container =>
  container.querySelector('a.link[href="#"]');

export const findAuthContent = container =>
  container.querySelector('.auth-content');

// Input Value Manipulation
export const setNativeInputValue = (input, value) => {
  const valueSetter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  ).set;

  valueSetter.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

export const setNativeSelectValue = (select, value) => {
  const valueSetter = Object.getOwnPropertyDescriptor(
    HTMLSelectElement.prototype,
    'value'
  ).set;

  valueSetter.call(select, value);
  select.dispatchEvent(new Event('change', { bubbles: true }));
};

export const setRememberMeCheckbox = (container, checked) => {
  const checkbox = findRememberMeCheckbox(container);
  if (!checkbox || checkbox.checked === checked) return;

  checkbox.checked = checked;
  checkbox.dispatchEvent(new Event('change', { bubbles: true }));
};

// Config Helpers
export const getGoogleIdpId = config =>
  config?.idps?.find(idp => idp.type === 'GOOGLE')?.id;

// View State History Helpers
export const getPreviousViewState = history =>
  history.length >= 2 ? history[history.length - 2] : null;

export const canNavigateBack = history => history.length >= 2;

// MFA Customizations (pure function, no side effects on React state)
export const applyMfaCustomizations = () => {
  const mfaTexts = getMfaTranslations();

  addedSubTitleForSMSAuthScreen({
    smsSubTitleScreenMessage: mfaTexts.sms.subtitle,
    setupSMS: mfaTexts.sms.setupButton,
  });

  changeMFAButtonsText([
    {
      tag: 'p',
      text: mfaTexts.email.description,
      buttonText: mfaTexts.email.setupButton,
    },
    {
      tag: 'p',
      text: mfaTexts.sms.description,
      buttonText: mfaTexts.sms.setupButton,
    },
    {
      tag: 'p',
      text: mfaTexts.googleAuth.description,
      buttonText: mfaTexts.googleAuth.setupButton,
    },
  ]);
};

export const MFA_FACTOR_MAP = {
  GOOGLE_AUTH: 'googleAuthenticator',
  SMS: 'sms',
  EMAIL: 'email',
};

export const MFA_FACTOR_KEYS = Object.values(MFA_FACTOR_MAP);

const MFA_FACTOR_REVERSE_MAP = Object.fromEntries(Object.entries(MFA_FACTOR_MAP).map(([k, v]) => [v, k]));

export const getAvailableMfaFactors = container => {
  const rows = container.querySelectorAll('li.enroll-factor-row[data-se]');
  const factors = new Set();

  rows.forEach(row => {
    const dataSe = row.getAttribute('data-se');
    const key = MFA_FACTOR_MAP[dataSe];
    if (key) factors.add(key);
  });

  return factors;
};

export const clickMfaEnrollButton = (container, factorKey) => {
  const dataSe = MFA_FACTOR_REVERSE_MAP[factorKey];
  if (!dataSe) return false;

  const button = container.querySelector(`li[data-se="${dataSe}"] a.button.link-button`);
  if (!button) return false;

  button.click();
  return true;
};

// Google Authenticator (TOTP) Enrollment Helpers
const TOTP_QR_CODE_SELECTORS = [
  '.qrcode-wrap img',
  'img[data-se="qrcode"]',
  '.qrcode-image',
];

const TOTP_MANUAL_KEY_SELECTORS = [
  'input[name="sharedSecret"]',
  'input.text-field-disabled',
  'p.shared-key',
  '.shared-key',
  '[data-se="shared-key"]',
];

const TOTP_PASSCODE_SELECTORS = [
  'input[name="passCode"]',
  'input[name="credentials.passcode"]',
  'input[name="answer"]',
];

export const getTotpQrCodeSrc = container => {
  const img = findElementBySelectors(container, TOTP_QR_CODE_SELECTORS);
  return img?.src || null;
};

export const getTotpManualSetupKey = container => {
  const keyEl = findElementBySelectors(container, TOTP_MANUAL_KEY_SELECTORS);
  if (!keyEl) return null;
  return keyEl.value?.trim() || keyEl.textContent?.trim() || null;
};

export const getTotpEnrollmentData = container => ({
  qrCodeSrc: getTotpQrCodeSrc(container),
  manualSetupKey: getTotpManualSetupKey(container),
});

export const clickCantScanLink = container => {
  const link = container.querySelector('a[data-se="manual-setup"]');
  if (!link) return false;

  link.click();
  return true;
};

export const clickTotpNextButton = container => {
  const submitButton = findSubmitButton(container);
  if (!submitButton) return false;

  submitButton.click();
  return true;
};

const setRememberDeviceCheckbox = (container, factorDataSe, checked) => {
  const cb = container.querySelector(`.mfa-verify form[data-se="${factorDataSe}"] input[type="checkbox"][name="rememberDevice"]`);
  if (!cb || cb.checked === Boolean(checked)) return;
  cb.click();
};

export const submitTotpCode = (container, code, rememberDevice = false) => {
  const passCodeInput = findElementBySelectors(
    container,
    TOTP_PASSCODE_SELECTORS
  );
  const submitButton = findSubmitButton(container);

  if (!passCodeInput || !submitButton) {
    return { success: false };
  }

  setNativeInputValue(passCodeInput, code);
  setRememberDeviceCheckbox(container, 'factor-totp', rememberDevice);
  submitButton.click();

  return { success: true };
};

// Email MFA Helpers
export const findMfaCodeInput = container =>
  container.querySelector('input[name="passCode"], input[name="credentials.passcode"], input[name="answer"]');

export const submitMfaCode = (container, code, rememberDevice = false) => {
  const codeInput = findMfaCodeInput(container);
  const submitBtn = findSubmitButton(container);
  if (!codeInput || !submitBtn) return { success: false };

  setNativeInputValue(codeInput, code);
  setRememberDeviceCheckbox(container, 'factor-email', rememberDevice);
  submitBtn.click();
  return { success: true };
};

export const clickSendEmailCode = container => {
  const btn = findSubmitButton(container);
  if (!btn) return false;
  btn.click();
  return true;
};

export const clickResendEmailCode = container => {
  const link = container.querySelector('a.resend-email-btn');
  if (!link) return false;
  link.click();
  return true;
};

// Password Reset Form Helpers
const NEW_PASSWORD_SELECTORS = ['input[name="newPassword"]'];

const CONFIRM_PASSWORD_SELECTORS = ['input[name="confirmPassword"]'];

export const findNewPasswordInput = container =>
  findElementBySelectors(container, NEW_PASSWORD_SELECTORS);

export const findConfirmPasswordInput = container =>
  findElementBySelectors(container, CONFIRM_PASSWORD_SELECTORS);

export const getPasswordResetFormElements = container => {
  const newPasswordInput = findNewPasswordInput(container);
  const confirmPasswordInput = findConfirmPasswordInput(container);
  const submitButton = findSubmitButton(container);

  return {
    newPasswordInput,
    confirmPasswordInput,
    submitButton,
    isReady: Boolean(newPasswordInput && submitButton),
  };
};

const OLD_PASSWORD_SELECTORS = ['input[name="oldPassword"]'];

export const findOldPasswordInput = container =>
  findElementBySelectors(container, OLD_PASSWORD_SELECTORS);

export const getPasswordExpiredFormElements = container => {
  const oldPasswordInput = findOldPasswordInput(container);
  const newPasswordInput = findNewPasswordInput(container);
  const confirmPasswordInput = findConfirmPasswordInput(container);
  const submitButton = findSubmitButton(container);

  return {
    oldPasswordInput,
    newPasswordInput,
    confirmPasswordInput,
    submitButton,
    isReady: Boolean(oldPasswordInput &&
        newPasswordInput &&
        confirmPasswordInput &&
        submitButton),
  };
};

export const submitPasswordExpiredForm = (
  container,
  { oldPassword, newPassword, confirmPassword }
) => {
  const {
    oldPasswordInput,
    newPasswordInput,
    confirmPasswordInput,
    submitButton,
    isReady,
  } = getPasswordExpiredFormElements(container);

  if (!isReady) {
    return { success: false };
  }

  setNativeInputValue(oldPasswordInput, oldPassword);
  setNativeInputValue(newPasswordInput, newPassword);
  setNativeInputValue(confirmPasswordInput, confirmPassword);
  submitButton.click();

  return { success: true };
};

export const clickPasswordExpiredFooterLink = (container, selector) => {
  const link = container.querySelector(selector);
  if (!link) return false;
  link.click();
  return true;
};

const PASSWORD_EXPIRED_ROOT_SELECTORS = [
  '.password-expired',
  '.custom-password-expired',
];

const findPasswordExpiredRoot = container => {
  for (const selector of PASSWORD_EXPIRED_ROOT_SELECTORS) {
    const el = container.querySelector(selector);
    if (el) return el;
  }
  return null;
};

const getPasswordExpiredTitle = container => {
  const root = findPasswordExpiredRoot(container);
  const titleEl =
    root?.querySelector('.okta-form-title') || root?.querySelector('h2');
  return titleEl?.textContent?.trim();
};

const parseDaysUntilExpiryFromTitle = title => {
  const match = title?.match(/(\d+)/);
  return match ? Number.parseInt(match[1], 10) : null;
};

const PASSWORD_REQUIREMENTS_HEADING_SELECTORS = [
  '.password-requirements--header',
  '.password-authenticator--heading',
];

const PASSWORD_REQUIREMENTS_LIST_ITEM_SELECTORS = [
  '.password-requirements--list .password-requirements--list-item',
  '.password-requirements--list li',
  '.password-authenticator--list li',
];

const getPasswordRequirements = container => {
  const root = findPasswordExpiredRoot(container);
  if (!root) return null;

  const heading = PASSWORD_REQUIREMENTS_HEADING_SELECTORS.map(sel =>
    root.querySelector(sel)?.textContent?.trim()).find(Boolean);

  const itemEls =
    PASSWORD_REQUIREMENTS_LIST_ITEM_SELECTORS.map(sel =>
      Array.from(root.querySelectorAll(sel))).find(list => list.length > 0) || [];

  const items = itemEls.map(li => li.textContent?.trim()).filter(Boolean);

  if (!heading && items.length === 0) return null;
  return { heading, items };
};

export const getPasswordExpiryInfo = container => {
  const canRemindLater = Boolean(container.querySelector('.js-skip'));
  const hasPasswordExpiredView = Boolean(findPasswordExpiredRoot(container));
  const hasExpired = hasPasswordExpiredView && !canRemindLater;
  const isExpiringSoon = canRemindLater;

  let daysUntilExpiry = null;
  let isExpiringToday = false;

  if (canRemindLater) {
    const parsed = parseDaysUntilExpiryFromTitle(getPasswordExpiredTitle(container));
    isExpiringToday = parsed === null;
    daysUntilExpiry = isExpiringToday ? 0 : parsed;
  }

  return {
    daysUntilExpiry,
    isExpiringToday,
    isExpiringSoon,
    hasExpired,
    canRemindLater,
    requirements: getPasswordRequirements(container),
  };
};

export const submitPasswordResetForm = (container, password) => {
  const {
    newPasswordInput,
    confirmPasswordInput,
    submitButton,
    isReady,
  } = getPasswordResetFormElements(container);

  if (!isReady) {
    return { success: false };
  }

  setNativeInputValue(newPasswordInput, password);
  if (confirmPasswordInput) {
    setNativeInputValue(confirmPasswordInput, password);
  }
  submitButton.click();

  return { success: true };
};

// Credentials Form Helpers
export const getCredentialsFormElements = container => {
  const usernameInput = findUsernameInput(container);
  const passwordInput = findPasswordInput(container);
  const submitButton = findSubmitButton(container);

  return {
    usernameInput,
    passwordInput,
    submitButton,
    isReady: Boolean(usernameInput && passwordInput && submitButton),
  };
};

export const getCredentialsFromForm = container => {
  const { usernameInput } = getCredentialsFormElements(container);
  const rememberCheckbox = findRememberMeCheckbox(container);

  return {
    email: usernameInput?.value?.trim() || '',
    rememberMe: rememberCheckbox?.checked,
  };
};

export const submitCredentialsToForm = (
  container,
  username,
  password,
  rememberMe
) => {
  const {
    usernameInput,
    passwordInput,
    submitButton,
    isReady,
  } = getCredentialsFormElements(container);

  if (!isReady) {
    return { success: false };
  }

  setNativeInputValue(usernameInput, username);
  setNativeInputValue(passwordInput, password);
  if (typeof rememberMe === 'boolean') {
    setRememberMeCheckbox(container, rememberMe);
  }
  submitButton.click();

  return { success: true };
};

// SMS MFA DOM Helpers
const SMS_SEND_BUTTON_SELECTOR = '.sms-request-button';
const SMS_COUNTRY_SELECT_SELECTOR = 'select[name="countryCode"]';
const SMS_PHONE_INPUT_SELECTOR = 'input[name="phoneNumber"]';
const SMS_PASSCODE_INPUT_SELECTOR = 'input[name="passCode"]';
const SMS_VERIFY_BUTTON_SELECTOR = '.o-form-button-bar input[type="submit"]';

export const setSmsCountryCode = (container, isoCode) => {
  const select = container.querySelector(SMS_COUNTRY_SELECT_SELECTOR);
  if (!select) return false;

  setNativeSelectValue(select, isoCode);
  select.dispatchEvent(new CustomEvent('chosen:updated', { bubbles: true }));

  return true;
};

export const setSmsPhoneNumber = (container, phone) => {
  const input = container.querySelector(SMS_PHONE_INPUT_SELECTOR);
  if (!input) return false;

  setNativeInputValue(input, phone);
  return true;
};

export const clickSmsSendCode = container => {
  const buttons = container.querySelectorAll(SMS_SEND_BUTTON_SELECTOR);
  const sendBtn = buttons[0];
  if (!sendBtn) return false;

  sendBtn.click();
  return true;
};

export const clickSmsResendCode = container => {
  const buttons = container.querySelectorAll(SMS_SEND_BUTTON_SELECTOR);
  if (buttons.length === 0) return false;

  buttons[buttons.length - 1].click();
  return true;
};

export const isSmsCodeInputVisible = container => {
  const input = container.querySelector(SMS_PASSCODE_INPUT_SELECTOR);
  if (!input) return false;

  const fieldset = input.closest('.o-form-fieldset');
  if (!fieldset) return false;

  return fieldset.style.display !== 'none';
};

const SMS_CODE_INPUT_SELECTOR = `${SMS_PASSCODE_INPUT_SELECTOR}, input[name="answer"]`;

export const submitSmsVerificationCode = (
  container,
  code,
  rememberDevice = false
) => {
  const codeInput = container.querySelector(SMS_CODE_INPUT_SELECTOR);
  const verifyBtn = container.querySelector(SMS_VERIFY_BUTTON_SELECTOR);
  if (!codeInput || !verifyBtn) return { success: false };

  setNativeInputValue(codeInput, code);
  setRememberDeviceCheckbox(container, 'factor-sms', rememberDevice);
  verifyBtn.click();
  return { success: true };
};

// MFA Verify Factor Detection (for `mfa-verify` state)

export const MFA_VERIFY_FACTOR_TYPES = {
  SMS: 'sms',
  EMAIL: 'email',
  GOOGLE_AUTH: 'totp',
};

export const detectMfaVerifyFactorType = container => {
  const form = container.querySelector('.mfa-verify form[data-se]');
  if (!form) return null;

  const dataSe = form.getAttribute('data-se');
  if (dataSe === 'factor-sms') return MFA_VERIFY_FACTOR_TYPES.SMS;
  if (dataSe === 'factor-email') return MFA_VERIFY_FACTOR_TYPES.EMAIL;
  if (dataSe === 'factor-totp') return MFA_VERIFY_FACTOR_TYPES.GOOGLE_AUTH;
  return null;
};

const MFA_VERIFY_STATE_BY_FACTOR = {
  [MFA_VERIFY_FACTOR_TYPES.SMS]: WIDGET_STATES.MFA_VERIFY_SMS,
  [MFA_VERIFY_FACTOR_TYPES.EMAIL]: WIDGET_STATES.MFA_VERIFY_EMAIL,
  [MFA_VERIFY_FACTOR_TYPES.GOOGLE_AUTH]: WIDGET_STATES.MFA_VERIFY_GOOGLE_AUTH,
};

export const resolveViewState = (rawController, container) => {
  const normalized = rawController?.toLowerCase();

  if (normalized === 'custom-password-expired') {
    return WIDGET_STATES.PASSWORD_EXPIRED;
  }

  if (normalized !== WIDGET_STATES.MFA_VERIFY) return rawController;

  const factorType = detectMfaVerifyFactorType(container);
  return MFA_VERIFY_STATE_BY_FACTOR[factorType] || rawController;
};

export const getOktaFormErrorText = container => {
  const errorEl =
    container.querySelector('.o-form-has-errors .o-form-error-container') ||
    container.querySelector('.o-form-has-errors .okta-form-input-error') ||
    container.querySelector('.o-form-has-errors .infobox-error');
  return errorEl?.textContent?.trim() || '';
};

export const getSmsVerifyMaskedPhone = container => {
  const subtitle = container.querySelector('.mfa-verify .okta-form-subtitle');
  if (!subtitle) return '';

  return subtitle.textContent.trim().replace(/^\(|\)$/g, '');
};
